import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { thumbnailUrl, FALLBACK_IMAGE } from '../../../lib/imageUtils';

interface Vehicle {
  uuid: string;
  id?: string | number;
  year: number;
  make: string;
  model: string;
  trim?: string;
  price: number;
  mileage: number;
  images?: string[];
  image?: string;
  bodyType?: string;
  fuelType?: string;
  transmission?: string;
  drivetrain?: string;
  exteriorColor?: string;
  interiorColor?: string;
  stock_number?: string;
  description?: string;
  hot_deal?: boolean;
  status?: string;
  features?: string[];
}

interface QuickViewModalProps {
  vehicle: Vehicle | null;
  onClose: () => void;
}

export default function QuickViewModal({ vehicle, onClose }: QuickViewModalProps) {
  const navigate = useNavigate();
  const [activeImg, setActiveImg] = useState(0);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setActiveImg(0);
    setImgError(false);
  }, [vehicle?.uuid]);

  useEffect(() => {
    if (!vehicle) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [vehicle, onClose]);

  if (!vehicle) return null;

  const images: string[] = Array.isArray(vehicle.images) && vehicle.images.length > 0
    ? vehicle.images
    : vehicle.image
    ? [vehicle.image]
    : [];

  const rawSrc = images[activeImg] || '';
  const imgSrc = imgError || !rawSrc ? FALLBACK_IMAGE : thumbnailUrl(rawSrc);

  const vehicleLabel = `${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? ' ' + vehicle.trim : ''}`;
  const detailUrl = `/inventory/${vehicle.uuid}`;
  const applyUrl = `/apply-for-financing?vehicle=${encodeURIComponent(vehicleLabel)}`;

  const monthly = (() => {
    if (!vehicle.price || vehicle.price <= 0) return 0;
    const loan = Math.max(vehicle.price - Math.round(vehicle.price * 0.1), 0);
    const r = 5.9 / 100 / 12;
    const n = 60;
    if (r === 0) return loan / n;
    return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  })();

  const specs = [
    { icon: 'ri-dashboard-line', label: 'Mileage', value: vehicle.mileage ? `${vehicle.mileage.toLocaleString()} mi` : null },
    { icon: 'ri-gas-station-line', label: 'Fuel', value: vehicle.fuelType || null },
    { icon: 'ri-settings-3-line', label: 'Transmission', value: vehicle.transmission || null },
    { icon: 'ri-steering-2-line', label: 'Drivetrain', value: vehicle.drivetrain || null },
    { icon: 'ri-palette-line', label: 'Ext. Color', value: vehicle.exteriorColor || null },
    { icon: 'ri-car-line', label: 'Body', value: vehicle.bodyType || null },
  ].filter(s => s.value);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Quick view: ${vehicleLabel}`}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full sm:max-w-4xl bg-white sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95dvh] sm:max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            {vehicle.hot_deal && (
              <span className="flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                <i className="ri-fire-fill" /> HOT DEAL
              </span>
            )}
            <h2 className="text-lg font-black text-[#1A2332]" style={{ fontFamily: 'Oswald, sans-serif' }}>
              {vehicleLabel}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer text-[#1A2332]"
            aria-label="Close quick view"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto flex-1">
          <div className="flex flex-col sm:flex-row">

            {/* Left — Gallery */}
            <div className="sm:w-[52%] flex-shrink-0 bg-slate-50">
              {/* Main image */}
              <div className="relative w-full bg-slate-100" style={{ paddingBottom: '66.66%' }}>
                <img
                  src={imgSrc}
                  alt={vehicleLabel}
                  className="absolute inset-0 w-full h-full object-cover object-center"
                  onError={() => setImgError(true)}
                />
                {/* Photo count */}
                {images.length > 1 && (
                  <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                    <i className="ri-camera-line text-xs" />
                    {activeImg + 1} / {images.length}
                  </div>
                )}
                {/* Nav arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setActiveImg(i => (i - 1 + images.length) % images.length)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors cursor-pointer"
                      aria-label="Previous photo"
                    >
                      <i className="ri-arrow-left-s-line text-lg" />
                    </button>
                    <button
                      onClick={() => setActiveImg(i => (i + 1) % images.length)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-black/50 hover:bg-black/70 text-white rounded-full transition-colors cursor-pointer"
                      aria-label="Next photo"
                    >
                      <i className="ri-arrow-right-s-line text-lg" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-1.5 p-2.5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                  {images.slice(0, 8).map((src, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImg(idx)}
                      className={`flex-shrink-0 w-14 h-10 rounded-md overflow-hidden border-2 transition-all cursor-pointer ${
                        activeImg === idx ? 'border-[#E8B84B] opacity-100' : 'border-transparent opacity-60 hover:opacity-90'
                      }`}
                      aria-label={`Photo ${idx + 1}`}
                    >
                      <img
                        src={thumbnailUrl(src)}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                  {images.length > 8 && (
                    <Link
                      to={detailUrl}
                      className="flex-shrink-0 w-14 h-10 rounded-md bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 hover:bg-slate-300 transition-colors cursor-pointer"
                    >
                      +{images.length - 8}
                    </Link>
                  )}
                </div>
              )}

              {/* Dog rescue strip */}
              <div className="mx-2.5 mb-2.5 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <div className="w-5 h-5 flex items-center justify-center bg-amber-500 rounded-full flex-shrink-0">
                  <i className="ri-heart-fill text-white text-[9px]" />
                </div>
                <p className="text-amber-800 text-xs font-medium">$100 donated to Oregon Dog Rescue with every sale</p>
              </div>
            </div>

            {/* Right — Details */}
            <div className="flex-1 p-5 flex flex-col gap-4">

              {/* Price block */}
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-3xl font-black text-[#1A2332]" style={{ fontFamily: 'Oswald, sans-serif' }}>
                    ${vehicle.price?.toLocaleString()}
                  </div>
                  {monthly > 0 && (
                    <div className="text-sm text-amber-600 font-semibold mt-0.5">
                      Est. ${Math.round(monthly).toLocaleString()}/mo · 60 mo @ 5.9%
                    </div>
                  )}
                </div>
                {vehicle.stock_number && (
                  <div className="text-right">
                    <p className="text-[10px] text-slate-400 uppercase tracking-wide">Stock #</p>
                    <p className="text-sm font-mono font-semibold text-slate-600">{vehicle.stock_number}</p>
                  </div>
                )}
              </div>

              {/* Specs grid */}
              {specs.length > 0 && (
                <div className="grid grid-cols-2 gap-2">
                  {specs.map(spec => (
                    <div key={spec.label} className="flex items-center gap-2.5 bg-[#F7F5F0] rounded-lg px-3 py-2.5">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <i className={`${spec.icon} text-[#E8B84B] text-base`} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide leading-none">{spec.label}</p>
                        <p className="text-sm font-semibold text-[#1A2332] leading-tight mt-0.5">{spec.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Description snippet */}
              {vehicle.description && (
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-1">About this vehicle</p>
                  <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">{vehicle.description}</p>
                </div>
              )}

              {/* Trust badges */}
              <div className="flex flex-wrap gap-2">
                {[
                  { icon: 'ri-shield-check-line', text: 'Inspected' },
                  { icon: 'ri-price-tag-3-line', text: 'Broker Priced' },
                  { icon: 'ri-truck-line', text: 'Free Delivery' },
                ].map(b => (
                  <div key={b.text} className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1">
                    <i className={`${b.icon} text-emerald-600 text-xs`} />
                    <span className="text-emerald-700 text-xs font-semibold">{b.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA buttons */}
              <div className="flex flex-col gap-2.5 mt-auto pt-2">
                <Link
                  to={detailUrl}
                  className="w-full flex items-center justify-center gap-2 bg-[#1A2332] text-white font-bold py-3 rounded-xl hover:bg-[#2d3f55] transition-colors text-sm whitespace-nowrap"
                >
                  <i className="ri-eye-line" />
                  View Full Details &amp; All Photos
                </Link>
                <Link
                  to={applyUrl}
                  className="w-full flex items-center justify-center gap-2 font-bold py-3 rounded-xl transition-colors text-sm whitespace-nowrap"
                  style={{ background: '#E8B84B', color: '#1A2332' }}
                >
                  <i className="ri-bank-card-line" />
                  Get Pre-Approved
                </Link>
                <button
                  onClick={() => { onClose(); navigate('/contact'); }}
                  className="w-full flex items-center justify-center gap-2 bg-slate-100 text-[#1A2332] font-semibold py-2.5 rounded-xl hover:bg-slate-200 transition-colors text-sm whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  Ask a Question
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
